import { Link } from "react-router-dom";
import { Users, Settings, Fuel, ArrowRight, CalendarCheck, MessageCircle } from "lucide-react";
import { PHP } from "@/lib/api";
import { waLink, waVehicleMessage } from "@/lib/contact";

export default function VehicleCard({ vehicle, index = 0 }) {
  const v = vehicle;
  const promo = Number(v.promo_rate) > 0 && Number(v.promo_rate) < Number(v.daily_rate);
  return (
    <div
      data-testid={`vehicle-card-${v.id}`}
      className="card-lift group relative overflow-hidden rounded-2xl border border-gold-500/15 bg-ink-800/60 flex flex-col"
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <Link to={`/vehicle/${v.id}`} className="relative aspect-[4/3] overflow-hidden bg-ink-900 block">
        <img
          src={v.image}
          alt={`${v.name} rental in Puerto Princesa, Palawan`}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/30 to-transparent opacity-90" />
        <div className="absolute top-4 left-4 flex gap-2">
          <span className="spec-pill capitalize" data-testid={`vehicle-type-${v.id}`}>{v.category}</span>
          {promo && (
            <span className="text-[10px] uppercase tracking-widest bg-green-500/90 text-ink-900 px-2.5 py-1 rounded-full font-semibold">
              {v.promo_label || "Promo"}
            </span>
          )}
        </div>
      </Link>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.25em] text-gold-500/80 mb-1 font-sans-display">{v.brand}</p>
            <Link to={`/vehicle/${v.id}`}>
              <h3 className="font-display text-2xl leading-tight text-foreground group-hover:text-gold-200 transition-colors truncate">
                {v.model}
              </h3>
            </Link>
            {v.year && <p className="text-xs text-foreground/40 mt-0.5">{v.year}</p>}
          </div>
          <div className="text-right flex-shrink-0">
            {promo ? (
              <>
                <p className="text-[11px] text-foreground/40 line-through">{PHP(v.daily_rate)}</p>
                <p className="font-display text-2xl text-green-300 leading-none mt-0.5">{PHP(v.promo_rate)}</p>
              </>
            ) : (
              <>
                <p className="text-[10px] uppercase tracking-widest text-foreground/40">From</p>
                <p className="font-display text-2xl text-gold-300 leading-none mt-1">{PHP(v.daily_rate)}</p>
              </>
            )}
            <p className="text-[10px] text-foreground/40 mt-0.5">/ day</p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5">
          {v.seats && (
            <span className="inline-flex items-center gap-1.5 text-xs text-foreground/60">
              <Users className="w-3.5 h-3.5 text-gold-500/80" /> {v.seats} seats
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 text-xs text-foreground/60 capitalize">
            <Settings className="w-3.5 h-3.5 text-gold-500/80" /> {v.transmission}
          </span>
          {v.drivetrain && (
            <span className="inline-flex items-center gap-1.5 text-xs text-foreground/60">
              <Fuel className="w-3.5 h-3.5 text-gold-500/80" /> {v.drivetrain}
            </span>
          )}
        </div>

        {v.features?.length > 0 && (
          <p className="mt-2 text-[11px] text-foreground/45 truncate">{v.features.slice(0, 4).join(" · ")}</p>
        )}

        <div className="mt-4 pt-4 border-t border-gold-500/10 grid grid-cols-[1fr_auto_auto] gap-2 items-stretch">
          <Link to={`/book/${v.id}`} className="btn-gold !py-2.5 !px-3 !text-[11px] justify-center" data-testid={`reserve-now-${v.id}`}>
            Reserve Now <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <Link to={`/vehicle/${v.id}`} className="btn-outline-gold !py-2.5 !px-3 !text-[11px]" data-testid={`check-availability-${v.id}`} title="Check availability">
            <CalendarCheck className="w-4 h-4" />
          </Link>
          <a
            href={waLink(waVehicleMessage(v))}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center px-3 rounded-full border border-green-500/40 text-green-300 hover:bg-green-500/15 transition"
            data-testid={`whatsapp-${v.id}`}
            title="Ask on WhatsApp"
          >
            <MessageCircle className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}
